import { NavItem } from 'components/top_bar/NavBar';
import { RouteTab } from 'custom_hooks/data_hooks/data_source/useRouteTabs';
import { HTMLAttributes } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export const NavTabLink = styled(NavLink)<Readonly<{ $padding?: string }>>`
    display: flex;
    box-sizing: border-box;
    height: 100%;
    align-items: center;
    padding-top: ${({ $padding }) => $padding ?? '1rem'};
    padding-bottom: ${({ $padding }) => $padding ?? '1rem'};
    font-size: 1.4rem;
    font-weight: 500;
    color: ${tokens.text.primary};
    text-decoration: none;
    border-bottom: 2px solid transparent;
    white-space: nowrap;

    &[aria-current] {
        border-color: ${tokens.primary.default};
        color: ${tokens.primary.default};
    }
`;

const NavTab = styled.li`
    margin-left: 1rem;
    margin-right: 1rem;
    display: flex;
`;

/**
 * Replacement wrapper for <NavItem> when tabs component is used in a NavBar context.
 * Correctly aligns the borders and paddings to the nav bar as designed.
 */
export const NavItemTabs = styled(NavItem)`
    padding-top: 0;
    padding-bottom: 0;
    display: flex;
    align-items: stretch;
`;

const Tabs = styled.ul`
    list-style: none;
    display: flex;
    flex-direction: row;
    padding: 0;
    margin: 0;

    ${NavTab}:first-child {
        margin-left: 0;
    }

    ${NavTab}:last-child {
        margin-right: 0;
    }
`;

export type NavTabsProps = Pick<HTMLAttributes<HTMLUListElement>, 'className'> & {
    /**
     * List of tabs to display.
     */
    tabs: RouteTab[];
    /**
     * Class applied to the tab / list element which wraps the link.
     */
    tabClassName?: string;
    /**
     * Class applied to the link element inside the tab.
     */
    tabLinkClassName?: string;
    /**
     * Overwrites padding on tab link.
     */
    tabLinkPadding?: string;
};

export function NavTabs({
    tabs,
    tabClassName,
    tabLinkClassName,
    tabLinkPadding,
    ...rest
}: NavTabsProps) {
    return (
        <Tabs {...rest}>
            {tabs.map((tab) => (
                <NavTab key={tab.id} className={tabClassName}>
                    <NavTabLink
                        className={tabLinkClassName}
                        to={tab.url}
                        $padding={tabLinkPadding}
                    >
                        {tab.label}
                    </NavTabLink>
                </NavTab>
            ))}
        </Tabs>
    );
}
